/**
 * ContactAdminLauncher — global floating pill mounted once at the app
 * root. Shows on every page bottom-right (offset so it doesn't overlap
 * the DPDP cookie banner or the AI-assist bubble), opens the shared
 * ContactAdminModal on click.
 *
 * Header + drawer trigger the same modal via the ``useContactAdmin()``
 * context hook so we always have one modal instance, not many.
 */
import { createContext, useCallback, useContext, useState } from "react";
import { MessageCircle } from "lucide-react";
import ContactAdminModal from "@/components/ContactAdminModal";

const ContactAdminContext = createContext({ open: () => {} });

export function useContactAdmin() {
  return useContext(ContactAdminContext);
}

export function ContactAdminProvider({ children }) {
  const [modalOpen, setModalOpen] = useState(false);
  const open = useCallback(() => setModalOpen(true), []);
  return (
    <ContactAdminContext.Provider value={{ open }}>
      {children}
      <ContactAdminModal open={modalOpen} onOpenChange={setModalOpen} />
    </ContactAdminContext.Provider>
  );
}

/**
 * The floating pill. As of Sep 2026 it lives at the TOP-RIGHT of the
 * viewport (below the sticky navbar) instead of the bottom-left corner,
 * so it stops overlapping the Report / Link-me / AI-assist bubbles
 * that already crowd the bottom-right on the family-tree page.
 * Positioning is `sticky` inside a fixed-position wrapper so it stays
 * visible while scrolling every page.
 */
export function ContactAdminFloatingButton() {
  const { open } = useContactAdmin();
  return (
    <div
      className="fixed z-40 pointer-events-none"
      style={{
        // Sit just under the sticky navbar (~64 px tall on desktop,
        // ~56 px on mobile) so the pill floats in the empty header
        // margin instead of covering tree nodes at the bottom.
        top: "calc(env(safe-area-inset-top, 0px) + 84px)",
        right: 16,
      }}
    >
      <button
        type="button"
        onClick={open}
        data-testid="contact-admin-fab"
        aria-label="Contact admin"
        className="pointer-events-auto flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full shadow-lg hover:shadow-xl border transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#B25D38]"
        style={{
          background: "linear-gradient(135deg, #FFF3D6 0%, #F6C583 100%)",
          borderColor: "#E8DFCF",
          color: "#5A3B1F",
          fontWeight: 600,
        }}
      >
        <MessageCircle className="w-4 h-4" style={{ color: "#B25D38" }} />
        <span className="text-xs sm:text-sm">Contact admin</span>
      </button>
    </div>
  );
}
